// UnifiedChatShell — the chat-first workspace.
//
// One screen, every tool. The user's transcript is the single source of
// truth: switching modes (Chat / Image / Research / Analyze / Builder /
// Library) never clears it. Non-chat tools run a quick action and drop
// their output into the transcript as an assistant turn so the user can
// keep flowing without context-switching.
//
// The model picker lives at the top-right and applies to the next chat
// turn — switching mid-thread carries all prior messages forward, which
// is the whole "compare LLMs without losing memory" promise.
//
// Optional `playbook` prop renders a step strip above the transcript and
// lets the parent surface the "next move" affordance. The shell does NOT
// own playbook state; the caller threads it in.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Send,
  Loader2,
  Cpu,
  MessageSquare,
  ImageIcon,
  Globe,
  FileText,
  Wand2,
  Library as LibraryIcon,
  Sparkles,
  Bookmark,
  BookmarkCheck,
  X,
  Upload,
  Download,
  ChevronDown,
  Check,
  Plus,
  History,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { streamSSE, type ChatMsg } from "@/lib/streamSSE";
import { supabase } from "@/integrations/supabase/client";
import { toastError } from "@/lib/errorToast";
import { trackFunnel, trackDailyChallenge } from "@/lib/funnelTracker";
import { emitFirstOutput } from "@/lib/firstOutputBus";
import type { Playbook } from "@/data/playbooks";
import { useSavedPlaybooks } from "@/hooks/useSavedPlaybooks";
import NextTaskModal, {
  type NextTaskBlock,
  applyVariables,
  blockScopedVariables,
} from "@/components/chat/NextTaskModal";
import { ListChecks } from "lucide-react";
import { setTutorContext, clearTutorContext } from "@/lib/tutorContextBus";
import SaveAsPlaybookDialog from "@/components/chat/SaveAsPlaybookDialog";
import { BookmarkPlus } from "lucide-react";
import InShellXPBadge from "@/components/chat/InShellXPBadge";
import CreditBurnTicker from "@/components/chat/CreditBurnTicker";
import InShellUsageMeter from "@/components/chat/InShellUsageMeter";
import CreditOverageWarning from "@/components/chat/CreditOverageWarning";
import MessageCostBreakdown from "@/components/hub/MessageCostBreakdown";
import type { Usage } from "@/lib/streamSSE";
import NextStepSuggestions from "@/components/chat/NextStepSuggestions";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/hooks/useAuth";
import GenerateMvpDialog from "@/components/mvp/GenerateMvpDialog";
import ChatLibraryDialog, { type ChatLibraryTab } from "@/components/chat/ChatLibraryDialog";
import ChatCommandPalette from "@/components/chat/ChatCommandPalette";
import { Lock } from "lucide-react";
import { popChatSeed } from "@/lib/chatSeedBus";
import PlaybookChecklist from "@/components/chat/PlaybookChecklist";
import { StopCircle } from "lucide-react";
import { Command as CommandIcon } from "lucide-react";
import { AVAILABLE_MODELS } from "@/lib/llmModels";
import { setChatMode } from "@/lib/chatModeBus";
import {
  heuristicRecommend,
  LOW_CONFIDENCE,
  type Recommendation,
  type RecommenderMode,
} from "@/lib/chatRecommender";
import { useAutoSaveChat } from "@/hooks/useAutoSaveChat";
import { CHAT_TYPES, loadLastChatType, saveLastChatType, type ChatType } from "@/lib/chatType";
import { downscaleDataUrl } from "@/lib/imageDownscale";

// --- Guest gate -------------------------------------------------------
// Anonymous visitors get to preview the unified chat for a few messages
// before we hard-gate further sends behind signup. Counter is persisted
// per-browser in localStorage so a refresh / mode switch / playbook swap
// can't reset it. Signed-in users skip this entirely.
const GUEST_MSG_KEY = "hl_guest_messages";
const GUEST_FREE_LIMIT = 3;

const readGuestCount = (): number => {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(GUEST_MSG_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

const writeGuestCount = (n: number) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(GUEST_MSG_KEY, String(Math.max(0, n)));
  } catch {
    /* private mode */
  }
};

// --- Modes ------------------------------------------------------------

export type ShellMode =
  | "chat"
  | "image"
  | "research"
  | "analyze"
  | "builder"
  | "library";

const MODES: {
  id: ShellMode;
  label: string;
  icon: typeof MessageSquare;
  hint: string;
}[] = [
  { id: "chat", label: "Chat", icon: MessageSquare, hint: "Talk to any model" },
  { id: "image", label: "Image", icon: ImageIcon, hint: "Generate or edit" },
  { id: "research", label: "Research", icon: Globe, hint: "Web-grounded answers" },
  { id: "analyze", label: "Analyze", icon: FileText, hint: "Summarize / extract" },
  { id: "builder", label: "Builder", icon: Wand2, hint: "Compose better prompts" },
  { id: "library", label: "Library", icon: LibraryIcon, hint: "Forkable playbooks" },
];

// --- Models -----------------------------------------------------------

// Chat picker now reads from the shared AVAILABLE_MODELS registry so
// adding/removing a brain in `src/lib/llmModels.ts` automatically flows
// here. Server-side `hub-chat` already whitelists Anthropic + OpenRouter
// via the spread of ANTHROPIC_MODELS / OPENROUTER_MODELS, so every brain
// in the registry is callable from this dropdown.
const CHAT_MODELS = AVAILABLE_MODELS.map((m) => ({ id: m.id, label: m.label }));

const IMAGE_MODELS = [
  { id: "google/gemini-2.5-flash-image", label: "Nano Banana (fast)", caps: ["Remixes", "≤5 MB"] },
  { id: "google/gemini-3.1-flash-image-preview", label: "Nano Banana 2 (pro, fast)", caps: ["Remixes", "≤5 MB"] },
  { id: "google/gemini-3-pro-image-preview", label: "Nano Banana Pro (best)", caps: ["Remixes", "≤5 MB"] },
  { id: "openai/gpt-image-2", label: "OpenAI GPT-Image 2", caps: ["Remixes", "≤5 MB"] },
  { id: "openai/gpt-image-1-mini", label: "OpenAI GPT-Image 1 Mini (cheap)", caps: ["Remixes", "≤5 MB"] },
];

const RESEARCH_MODELS = [
  { id: "sonar", label: "Sonar (fast)" },
  { id: "sonar-pro", label: "Sonar Pro" },
  { id: "sonar-reasoning", label: "Sonar Reasoning" },
];

const ANALYZE_TASKS = [
  { id: "summarize", label: "Summarize" },
  { id: "explain", label: "Explain simply" },
  { id: "refactor", label: "Refactor code" },
  { id: "extract_action_items", label: "Extract action items" },
];

// --- Transcript turn shape -------------------------------------------

type Turn =
  | { id: string; role: "user"; content: string }
  | {
      id: string;
      role: "assistant";
      content: string;
      model?: string;
      kind?: "chat" | "image" | "research" | "analyze";
      image?: string | null;
      citations?: string[];
      streaming?: boolean;
      /** When this turn was emitted as part of a multi-block playbook
       *  step run, this carries the 1-indexed step number so the UI
       *  can render a "Step N · Research" badge above the bubble. */
      step?: number;
      /** True if the user cancelled the multi-block run before this
       *  block could finish — surfaces a small "Cancelled" tag and
       *  prevents downstream blocks from running. */
      cancelled?: boolean;
      /** Token usage returned by the gateway when the stream finished.
       *  Drives the per-message cost breakdown under the bubble — only
       *  populated for assistant turns that actually billed (chat /
       *  research / builder). */
      usage?: Usage;
    };

let turnCounter = 0;
const newId = () => `t${Date.now().toString(36)}-${++turnCounter}`;

// Markdown body for the in-chat /help message. Kept at module scope so
// we don't rebuild the string on every render and so the welcome
// auto-emit and the manual /help slash command share one source of
// truth. Lists the actions a user actually has — short, scannable, no
// marketing copy. Each row pairs the action with how to trigger it
// (slash command, ⌘K palette entry, or composer button).
const HELP_MESSAGE_BODY = `**Top things you can do from this chat**

- **Just ask** — type any question and pick a model from the top-right. Switching models keeps the full conversation, so you can compare answers without losing context.
- **\`/library\` [keyword]** — open the in-chat Library to browse community playbooks, prompts, guides, and research without leaving chat.
- **\`/recent\`** (or \`/history\`) — jump back into one of your saved chats.
- **\`/mvp\` <your idea>** — generate a one-page MVP spec from a single sentence.
- **\`/hint\`** — get a stepwise hint for today's daily challenge. Type it again to unlock the next, more specific hint (Nudge → Approach → Scaffold).
- **⌘K / Ctrl+K** — open the command palette to search AI tools, library entries, and people, or trigger any action (new chat, save playbook, account, settings).
- **Save as playbook** — once you have a useful exchange, hit **Save as playbook** under the composer to turn it into a reusable, shareable recipe tagged against the mastery skills you used.
- **Switch modes** — use the mode tabs (Chat · Image · Research · Analyze) to run a non-chat tool; the result drops back into this transcript so your context carries over.

Type **\`/help\`** any time to see this list again.`;

// --- Props ------------------------------------------------------------

interface Props {
  /** Optional playbook to surface as a step strip above the transcript. */
  playbook?: Playbook | null;
  /** Topic substituted into playbook step prompts. */
  topic?: string;
  /** Initial mode — defaults to chat. */
  initialMode?: ShellMode;
  /** Initial chat model — defaults to Gemini 3 Flash. */
  initialModel?: string;
  /** Optional caller-controlled "leave this shell" handler. */
  onExit?: () => void;
  /** Optional pre-seeded composer text (e.g. from the home search bar). */
  initialComposerText?: string;
}

// --- Component --------------------------------------------------------

export default function UnifiedChatShell({
  playbook = null,
  topic = "",
  initialMode = "chat",
  initialModel,
  onExit,
  initialComposerText = "",
}: Props) {
  const { user } = useAuth();
  const [mode, setMode] = useState<ShellMode>(initialMode);
  // Publish the active chat mode to the global Navbar so it can show a
  // tiny "you are here" badge. Cleared on unmount so other routes fall
  // back to their own route-derived label.
  useEffect(() => {
    setChatMode(mode);
    return () => setChatMode(null);
  }, [mode]);
  const [chatModel, setChatModel] = useState<string>(
    initialModel ?? CHAT_MODELS[0].id,
  );
  const [turns, setTurns] = useState<Turn[]>([]);
  // Caches the `chat_conversations` row id once auto-save (or share)
  // has minted one. Sharing/save flows reuse this so we never create
  // duplicate rows for the same active conversation.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [composer, setComposer] = useState(initialComposerText);
  const [busy, setBusy] = useState(false);
  // Pending mode-switch suggestion from the recommender. When set, a
  // chip appears above the composer; clicking it flips the active
  // mode and immediately resends the draft. Cleared on every send,
  // every mode change the user makes manually, and whenever the
  // composer is emptied (so a stale "looks like an image request"
  // chip can't haunt the next conversation).
  const [pendingSuggestion, setPendingSuggestion] = useState<Recommendation | null>(
    null,
  );
  // Tracks whether we've already auto-switched the model for the
  // current draft. Prevents the recommender from oscillating between
  // two model ids while the user is mid-sentence.
  const lastAutoSwitchedDraftRef = useRef<string>("");
  // `/mvp <idea>` slash command → opens the MVP generator dialog with
  // whatever the user typed after the command pre-filled as the idea.
  const [mvpOpen, setMvpOpen] = useState(false);
  const [mvpInitialIdea, setMvpInitialIdea] = useState("");
  // `/library` and `/recent` slash commands → open the in-chat Library
  // dialog so the user never has to leave the chat to browse community
  // entries or jump back to a recent saved chat. Anything after the
  // command is treated as a search seed.
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [libraryDialogTab, setLibraryDialogTab] = useState<ChatLibraryTab>("library");
  const [libraryDialogQuery, setLibraryDialogQuery] = useState("");
  // ⌘K / Ctrl+K command palette — universal jump for tools, library
  // entries, profiles and shell actions. Lives at the shell level so
  // every mode (chat / image / research / analyze) can trigger it.
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Mode-local state (kept here so switching modes preserves drafts).
  const [imageModel, setImageModel] = useState(IMAGE_MODELS[0].id);
  const [imageSource, setImageSource] = useState<string | null>(null);
  const [imageRemixOnly, setImageRemixOnly] = useState(false);
  const [researchModel, setResearchModel] = useState(RESEARCH_MODELS[0].id);
  const [analyzeTask, setAnalyzeTask] = useState(ANALYZE_TASKS[0].id);

  // Next-task modal — composes a multi-block run for a single playbook
  // step. We track which step the user opened the modal for so the
  // header shows context and the seeded chat block carries the right
  // default prompt.
  const [nextTaskOpen, setNextTaskOpen] = useState(false);
  const [nextTaskStepIdx, setNextTaskStepIdx] = useState(0);

  // ---- Multi-block run state ---------------------------------------
  // When the user kicks off a Next-Task stack, we keep an AbortController
  // around so a "Cancel remaining" pill can stop in-flight streaming and
  // skip the remaining blocks. `runStatus` drives that pill: it shows
  // which step is running and how many blocks remain so the user can
  // make an informed bail-out decision.
  const runAbortRef = useRef<AbortController | null>(null);
  const [runStatus, setRunStatus] = useState<{
    step: number;
    currentIndex: number; // 0-indexed block currently running
    total: number;
    currentKindLabel: string;
  } | null>(null);

  // Save-as-playbook dialog — captures the live transcript + tags it
  // against the 100-skill mastery path. We pass `turns` straight in;
  // the dialog snapshots them on each open so concurrent streaming
  // doesn't mutate the saved version.
  const [saveOpen, setSaveOpen] = useState(false);

  // Guest gate state — counter (persisted) + auth-modal trigger. We
  // re-read on focus/visibility so a signup completed in another tab
  // doesn't strand a stale counter here.
  const [guestCount, setGuestCount] = useState<number>(() => readGuestCount());
  const [authOpen, setAuthOpen] = useState(false);
  useEffect(() => {
    if (user) return; // signed-in users bypass entirely
    const refresh = () => setGuestCount(readGuestCount());
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user]);

  // Global ⌘K / Ctrl+K → toggle the in-chat command palette. We only
  // intercept when the modifier is held so plain "k" still types into
  // the composer; the keydown is suppressed (preventDefault) to avoid
  // browser default that opens the location bar in some Firefox builds.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isGuest = !user;
  const guestRemaining = Math.max(0, GUEST_FREE_LIMIT - guestCount);
  const guestGated = isGuest && guestRemaining <= 0;

  const transcriptRef = useRef<HTMLDivElement>(null);
  const { isSaved, toggle: toggleSaved } = useSavedPlaybooks();

  // Optional "loaded from …" pill + source string — set when we pop a
  // chat seed off the bus. Lets the user see what just got dropped into
  // their transcript (e.g. "challenge: Generate a playbook for X").
  const [seedPill, setSeedPill] = useState<{ label: string; source: string } | null>(null);

  // Chat type — Task / Quest / Challenge / Open. Picked by the user
  // before the first turn (selector hides once the conversation is in
  // progress so the label stays stable). Persisted across sessions so
  // a power user who always works in "Task" mode doesn't have to keep
  // re-picking. A seed (e.g. "quest:foo") overrides the default to the
  // matching type so the picker reflects where the chat came from.
  const [chatType, setChatTypeState] = useState<ChatType>(() => loadLastChatType());
  const setChatType = (t: ChatType) => {
    setChatTypeState(t);
    saveLastChatType(t);
  };

  // Silently persist this conversation to chat_conversations so it
  // shows up under "Recent chats" with an auto-derived title. Pure
  // background work — never blocks the UI, never overrides a title
  // the user picked manually in the Save dialog. See the hook's
  // header comment for the full rationale.
  useAutoSaveChat({
    turns: turns as Array<{ role: "user" | "assistant" | "system"; content: string; streaming?: boolean }>,
    model: chatModel,
    seedSource: seedPill?.source ?? null,
    seedTitle: seedPill?.label ?? null,
    chatType,
    conversationId,
    setConversationId,
  });

  // When a seed sets `autoSend: true` we need to fire `send()` once the
  // composer state has been hydrated with the draft. We can't call
  // `send()` synchronously inside the seed-consume effect because the
  // closure still sees the empty initial composer. A tiny ref-flag flips
  // the trigger; a follow-up effect that watches `composer` + this flag
  // fires `send()` exactly once.
  const pendingAutoSendRef = useRef(false);

  // Consume any pending chat seed exactly once on mount. Pop = read +
  // delete so a refresh gives a clean conversation. Idempotent: we only
  // seed when the transcript is empty — if the user already started
  // typing, we leave them alone.
  useEffect(() => {
    const pending = popChatSeed();
    if (!pending) return;
    // Seeds are explicit "load this brief into chat" handoffs (Coach
    // me / Apply this technique / etc.). If the user already had a
    // conversation, keeping it would silently swallow the seed — exactly
    // the bug reported when clicking Coach on Past Challenges. Wipe the
    // transcript and start fresh with the new primer instead.
    setTurns([
      {
        id: newId(),
        role: "assistant",
        content: pending.primer,
        kind: "chat",
        streaming: false,
      },
    ]);
    if (pending.draft) {
      // Replace the composer for the same reason — the seed *is* the
      // intended next message; preserving stale draft text would block
      // the auto-send path below.
      setComposer(pending.draft);
    }
    setSeedPill({
      label: pending.title || pending.source,
      source: pending.source,
    });
    // Sync the chat-type chip to the seed kind so the Recent list
    // groups it correctly even though the user never touched the
    // selector. We only override when the seed maps to a known
    // bucket — generic library/playbook seeds stay on whatever the
    // user previously had selected.
    const seedHead = pending.source.split(":")[0]?.toLowerCase();
    if (seedHead === "quest") setChatType("quest");
    else if (seedHead === "challenge" || seedHead === "past-challenge") setChatType("challenge");
    else if (seedHead === "task") setChatType("task");
    if (pending.autoSend && pending.draft) {
      pendingAutoSendRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Playbook swap handling ---------------------------------------
  //
  // The shell is mounted once on /home and reused as the user clicks
  // different rotating mastery chips. Without this effect, only the
  // *first* playbook ever shows up: `composer` and `chatModel` are
  // initialized with `useState(initialComposerText)` / `useState(initialModel)`
  // (one-shot), so a second click swaps the `playbook` prop but leaves the
  // composer pre-filled with the *previous* challenge's prompt — exactly the
  // bug the user reported ("clicking a new chip just pulls up the old
  // chat with the same thing in it").
  //
  // Fix: when `playbook?.slug` changes after mount, treat it as an
  // explicit "load a new challenge" event. We don't wipe the transcript
  // — the previous conversation is bumped down for later, with a clear
  // divider so the user knows *why* the topic just changed. Then we
  // hydrate the composer with the new step 1 prompt and switch the model
  // to whatever that step prefers.
  const lastSwappedSlugRef = useRef<string | null>(playbook?.slug ?? null);
  useEffect(() => {
    const nextSlug = playbook?.slug ?? null;
    if (nextSlug === lastSwappedSlugRef.current) return;
    // Skip the very first run — that's just the initial mount, which
    // the existing `initialComposerText` / seed effects already handle.
    if (lastSwappedSlugRef.current === null) {
      lastSwappedSlugRef.current = nextSlug;
      return;
    }
    lastSwappedSlugRef.current = nextSlug;
    if (!playbook) return;

    const firstStep = playbook.steps[0];
    const filled = firstStep
      ? firstStep.prompt.replace(
          /\{topic\}/g,
          topic.trim() || "(your topic here)",
        )
      : "";

    // Append a clear divider so the prior conversation is *bumped for
    // later*, not silently overwritten. Render as an assistant turn so
    // it flows with the existing transcript styling without needing a
    // new turn type.
    setTurns((prev) => [
      ...prev,
      {
        id: newId(),
        role: "assistant",
        kind: "chat",
        streaming: false,
        content:
          `---\n\n**🔄 Switched to: ${playbook.title}**\n\n` +
          `_Your previous challenge is bumped up for later — scroll up to revisit it any time._\n\n` +
          (playbook.tagline ? `${playbook.tagline}\n\n` : "") +
          `Hit **Send** to start step 1 below.`,
      },
    ]);
    setComposer(filled);
    if (firstStep) setChatModel(firstStep.model);
    setSeedPill({
      label: playbook.title,
      source: "challenge swap",
    });
  }, [playbook, topic]);

  // Auto-scroll to keep the latest turn visible. Composer is now pinned
  // at the TOP of the shell, so the transcript flows downward beneath
  // it. Scrolling to the top of the transcript surfaces the newest
  // message right under the composer (newer-first reading order).
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = 0;
  }, [turns]);

  // Publish current playbook/model state to the global Tutor FAB so the
  // tutor can give context-aware answers ("you're on step 2 of X using Y").
  // currentStep updates automatically as the user progresses; see the
  // dedicated effect below that depends on currentStepIndex.
  useEffect(() => {
    setTutorContext({
      currentPlaybook: playbook?.title,
      currentModel: chatModel,
    });
    return () =>
      clearTutorContext([
        "currentPlaybook",
        "currentStep",
        "currentModel",
        "recentMessages",
      ]);
  }, [playbook?.title, chatModel]);

  // ---- Append helpers ------------------------------------------------

  const appendUser = (text: string): Turn => {
    const t: Turn = { id: newId(), role: "user", content: text };
    setTurns((prev) => [...prev, t]);
    return t;
  };

  type AssistantKind = "chat" | "image" | "research" | "analyze";
  /**
   * Append an empty assistant bubble that will be filled by streaming.
   * The optional `step` argument tags the bubble with the playbook
   * step number it belongs to, so the transcript can render a clear
   * "Step 3 · Research" header above it (and again above the next
   * "Step 3 · Chat" bubble when the same step stacks blocks).
   */
  const appendAssistantPlaceholder = (
    kind: AssistantKind,
    step?: number,
  ): string => {
    const id = newId();
    setTurns((prev) => [
      ...prev,
      {
        id,
        role: "assistant",
        content: "",
        kind,
        streaming: true,
        step,
      },
    ]);
    return id;
  };

  const updateAssistant = (id: string, patch: Partial<Extract<Turn, { role: "assistant" }>>) => {
    setTurns((prev) =>
      prev.map((t) =>
        t.id === id && t.role === "assistant" ? ({ ...t, ...patch } as Turn) : t,
      ),
    );
  };

  // ---- Recommender helpers -------------------------------------------
  //
  // The recommender returns *suggestions*; how we apply them depends on
  // what changed:
  //   - **Model only**: silently switch + drop a single small system
  //     note into the transcript so the user knows what happened. UX
  //     decision (locked in with the user): "Auto-switch silently".
  //   - **Mode**: stash as `pendingSuggestion`. The chip above the
  //     composer shows "Looks like X — Switch to Y", clicking applies
  //     the mode and re-runs the same draft. UX decision: "Suggest +
  //     one-click switch, stay in chat".
  const noteId = useRef(0);
  const applyModelRecommendation = (rec: Recommendation, draft: string) => {
    if (!rec.model || rec.model === chatModel) return;
    if (lastAutoSwitchedDraftRef.current === draft) return;
    lastAutoSwitchedDraftRef.current = draft;
    const prevLabel = AVAILABLE_MODELS.find((m) => m.id === chatModel)?.label ?? chatModel;
    const nextLabel = AVAILABLE_MODELS.find((m) => m.id === rec.model)?.label ?? rec.model;
    setChatModel(rec.model);
    setTurns((prev) => [
      ...prev,
      {
        id: `note-${++noteId.current}`,
        role: "assistant",
        content: `_Switched model: **${prevLabel}** → **${nextLabel}**. ${rec.reason}_`,
        kind: "chat",
      },
    ]);
  };

  /**
   * Run the recommender for the *next* send. Returns true when a mode
   * change is pending and the caller should defer dispatch (so the
   * chip can render and the user can click it). Returns false when
   * either there's nothing to do or only the model changed (in which
   * case the silent swap has already been applied and dispatch should
   * proceed normally).
   */
  const runRecommender = async (draft: string): Promise<boolean> => {
    const historyLite = turns
      .filter((t) => !("streaming" in t) || !t.streaming)
      .slice(-6)
      .map((t) => ({
        role: t.role,
        content: typeof t.content === "string" ? t.content : "",
      }));
    const heuristic = heuristicRecommend({
      history: historyLite,
      draft,
      currentMode: mode as RecommenderMode,
      currentModel: chatModel,
    });

    // High-confidence heuristic short-circuit — no AI call needed.
    if (heuristic && heuristic.confidence >= LOW_CONFIDENCE) {
      return handleRecommendation(heuristic, draft);
    }

    // Heuristic was silent or unsure: ask the classifier. Failure is
    // non-blocking — if the function 401s or rate-limits we just
    // proceed with the user's current mode + model.
    try {
      const { data, error } = await supabase.functions.invoke("recommend-chat", {
        body: {
          history: historyLite,
          draft,
          currentMode: mode,
          currentModel: chatModel,
        },
      });
      if (error) return false;
      const rec = data?.recommendation as Recommendation | null | undefined;
      if (!rec) return false;
      // Don't undercut a high-confidence heuristic with a wishy-washy
      // model suggestion — only honour the classifier when it's at
      // least as confident as our floor.
      if ((rec.confidence ?? 0) < LOW_CONFIDENCE) return false;
      return handleRecommendation(rec, draft);
    } catch {
      return false;
    }
  };

  /**
   * Apply a recommendation: silent swap for model, deferred chip for
   * mode. Returns true when dispatch should be deferred.
   */
  const handleRecommendation = (rec: Recommendation, draft: string): boolean => {
    if (rec.mode && rec.mode !== mode) {
      setPendingSuggestion(rec);
      return true;
    }
    if (rec.model && rec.model !== chatModel) {
      applyModelRecommendation(rec, draft);
      return false;
    }
    return false;
  };

  /**
   * Accept the pending mode suggestion: switch the active mode and
   * immediately retry the draft so the user gets one-click teleport.
   */
  const acceptPendingSuggestion = () => {
    const rec = pendingSuggestion;
    if (!rec?.mode) return;
    setMode(rec.mode as ShellMode);
    setPendingSuggestion(null);
    // Defer to the next tick so the new mode is applied before send().
    window.setTimeout(() => {
      void send();
    }, 0);
  };

  // Build OpenAI-style history for chat. Skips empty / streaming turns.
  const historyForChat = (extraUser: string): ChatMsg[] => {
    const msgs: ChatMsg[] = [];
    for (const t of turns) {
      if (t.role === "user") msgs.push({ role: "user", content: t.content });
      else if (t.content.trim())
        msgs.push({ role: "assistant", content: t.content });
    }
    msgs.push({ role: "user", content: extraUser });
    return msgs;
  };

  // ---- Send dispatcher ----------------------------------------------

  const send = async () => {
    const text = composer.trim();
    if (!text || busy) return;
    // Slash command: `/mvp` (optionally followed by an idea) opens the
    // Generate MVP dialog instead of sending a chat turn. We clear the
    // composer so the command doesn't sit there after the dialog opens.
    const mvpMatch = text.match(/^\/mvp\b\s*([\s\S]*)$/i);
    if (mvpMatch) {
      setMvpInitialIdea(mvpMatch[1].trim());
      setMvpOpen(true);
      setComposer("");
      return;
    }
    // `/library [query]` — open the in-chat Library browser. Optional
    // trailing text seeds the search box.
    const libMatch = text.match(/^\/library\b\s*([\s\S]*)$/i);
    if (libMatch) {
      setLibraryDialogTab("library");
      setLibraryDialogQuery(libMatch[1].trim());
      setLibraryDialogOpen(true);
      setComposer("");
      return;
    }
    // `/recent [query]` — same dialog, jumps straight to the recent
    // saved-chats tab. Trailing text seeds the search.
    const recentMatch = text.match(/^\/(recent|history)\b\s*([\s\S]*)$/i);
    if (recentMatch) {
      setLibraryDialogTab("recent");
      setLibraryDialogQuery(recentMatch[2].trim());
      setLibraryDialogOpen(true);
      setComposer("");
      return;
    }
    // `/help` (or `/?`) — drop a contextual help message into the
    // transcript instead of routing to a docs page. Stays in-context so
    // the user can read it while their current chat is still onscreen.
    const helpMatch = text.match(/^\/(help|\?|commands)\b/i);
    if (helpMatch) {
      setTurns((prev) => [
        ...prev,
        {
          id: newId(),
          role: "assistant",
          content: HELP_MESSAGE_BODY,
          kind: "chat",
        },
      ]);
      setComposer("");
      return;
    }
    // `/hint` — fetch the next stepwise hint for today's daily
    // challenge and drop it into the transcript. Hints unlock
    // progressively (Nudge → Approach → Scaffold) and the per-user
    // unlock counter is persisted in localStorage scoped to the hack
    // id, so refreshing the page or switching modes can't reset
    // progress. We deliberately fetch lazily here (not on every shell
    // mount) since most chat sessions don't ask for hints — no point
    // making a network call until the user opts in.
    const hintMatch = text.match(/^\/hint\b/i);
    if (hintMatch) {
      setComposer("");
      setTurns((prev) => [
        ...prev,
        {
          id: newId(),
          role: "assistant",
          content: "_Fetching today's hint…_",
          kind: "chat",
        },
      ]);
      try {
        // Look up today's daily hack (same query as useDailyHack but
        // inline so we don't have to thread a hook through the shell).
        const today = new Date().toISOString().slice(0, 10);
        const { data: hackRows, error: hackErr } = await supabase
          .from("daily_hacks")
          .select("id, title")
          .lte("hack_date", today)
          .order("hack_date", { ascending: false })
          .limit(1);
        if (hackErr) throw hackErr;
        const hack = hackRows?.[0];
        if (!hack) {
          setTurns((prev) =>
            prev.map((t, i) =>
              i === prev.length - 1
                ? {
                    ...t,
                    content:
                      "No daily challenge is live right now. Check back tomorrow — or run any prompt and ask the model directly.",
                  }
                : t,
            ),
          );
          return;
        }
        // Compute the next hint to unlock (1-indexed, capped at 3).
        const storageKey = `hl_hint_unlocked_${hack.id}`;
        let unlocked = 0;
        try {
          const raw = window.localStorage.getItem(storageKey);
          unlocked = raw ? Math.max(0, Math.min(3, parseInt(raw, 10) || 0)) : 0;
        } catch {
          /* private mode → start at 0 */
        }
        const nextIndex = Math.min(unlocked + 1, 3);
        const { data, error } = await supabase.functions.invoke(
          "daily-hack-hint",
          { body: { hackId: hack.id, hintIndex: nextIndex } },
        );
        if (error) throw error;
        // Stall analytics — `hint_requested` is one of the strongest
        // signals that the instructional scaffolding isn't carrying
        // the user. We log the unlocked tier (1 = nudge, 2 = approach,
        // 3 = scaffold) so admins can see *which* hint level users
        // bottom out at.
        trackDailyChallenge("hint_requested", {
          hack_id: hack.id,
          hint_index: nextIndex,
        });
        const label = (data?.label as string) ?? "Hint";
        const body = (data?.body as string) ?? "";
        const total = (data?.totalHints as number) ?? 3;
        // Persist the new unlock level so subsequent /hint calls
        // advance, and once we hit 3 every future /hint just re-emits
        // the deepest scaffold (no further escalation possible).
        try {
          window.localStorage.setItem(storageKey, String(nextIndex));
        } catch {
          /* ignore */
        }
        const header = `**Hint ${nextIndex}/${total} · ${label}** — _${hack.title}_`;
        const footer =
          nextIndex < total
            ? `\n\n_Type **\`/hint\`** again to unlock hint ${nextIndex + 1}/${total}._`
            : `\n\n_That's the deepest hint. You've got this — run it and check the output against the goals checklist._`;
        setTurns((prev) =>
          prev.map((t, i) =>
            i === prev.length - 1
              ? { ...t, content: `${header}\n\n${body}${footer}` }
              : t,
          ),
        );
      } catch (err) {
        console.error("[/hint] failed", err);
        const msg =
          err instanceof Error && /402|payment/i.test(err.message)
            ? "AI credits exhausted — add funds in workspace settings to keep using hints."
            : err instanceof Error && /429|rate/i.test(err.message)
              ? "Rate limit hit. Try again in a few seconds."
              : "Couldn't fetch a hint just now. Try again in a moment.";
        setTurns((prev) =>
          prev.map((t, i) =>
            i === prev.length - 1 ? { ...t, content: msg } : t,
          ),
        );
      }
      return;
    }
    // Hard guest gate — once the free preview quota is exhausted, block
    // any further send and open the signup modal. We keep the composer
    // text intact so the user can fire it the moment they're back in.
    if (isGuest && guestCount >= GUEST_FREE_LIMIT) {
      setAuthOpen(true);
      toast("Create a free account to keep going — we'll save your work.", {
        icon: "🔒",
      });
      return;
    }
    // Run the recommender BEFORE we lock the composer or append the
    // user turn. If a mode-switch chip is needed, we want the draft to
    // stay in the composer so the click-to-accept handler can resend
    // it cleanly. Model-only suggestions silently mutate state and
    // fall through to dispatch.
    const deferred = await runRecommender(text);
    if (deferred) return;

    setComposer("");
    setBusy(true);
    appendUser(text);
    if (isGuest) {
      const next = guestCount + 1;
      setGuestCount(next);
      writeGuestCount(next);
    }

    try {
      if (mode === "chat") await runChat(text);
      else if (mode === "image") await runImage(text);
      else if (mode === "research") await runResearch(text);
      else if (mode === "analyze") await runAnalyze(text);
      else if (mode === "builder") await runBuilder(text);
      else if (mode === "library") {
        // Library mode is primarily a picker; the composer just acts as
        // a chat fallback so the user can ask "what should I fork?".
        await runChat(text);
      }
    } finally {
      setBusy(false);
    }
  };

  // Auto-send the seeded draft when a "click to chat" entry point asked
  // for it. Runs on the next tick after the seed effect populated the
  // composer; the ref-flag guarantees we only ever fire once even if
  // `composer` is later edited by the user.
  useEffect(() => {
    if (!pendingAutoSendRef.current) return;
    if (!composer.trim() || busy) return;
    pendingAutoSendRef.current = false;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer]);

  // ---- Mode runners --------------------------------------------------

  const runChat = async (
    text: string,
    opts: { step?: number; signal?: AbortSignal } = {},
  ) => {
    const sendId = `chat_${Date.now()}`;
    trackFunnel("generation_start", { surface: "chat", model: chatModel, dedupeKey: sendId });
    const aid = appendAssistantPlaceholder("chat", opts.step);
    let acc = "";
    try {
      await streamSSE({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hub-chat`,
        body: { messages: historyForChat(text), model: chatModel },
        onDelta: (chunk) => {
          acc += chunk;
          updateAssistant(aid, { content: acc, model: chatModel });
        },
        onUsage: (usage) => updateAssistant(aid, { usage }),
        signal: opts.signal,
      });
      updateAssistant(aid, { content: acc, model: chatModel, streaming: false });
      trackFunnel("generation_output", { surface: "chat", model: chatModel, dedupeKey: sendId });
      emitFirstOutput("chat");
    } catch (e) {
      const err = e as Error;
      // Treat abort cleanly — keep whatever streamed so far and tag the
      // turn as cancelled so the UI shows it visibly stopped.
      if (opts.signal?.aborted || err.name === "AbortError") {
        updateAssistant(aid, {
          content: acc,
          streaming: false,
          cancelled: true,
          model: chatModel,
        });
        return;
      }
      updateAssistant(aid, {
        content: acc || `_Error: ${err.message || "chat failed"}_`,
        streaming: false,
      });
      toastError(err.message || "Chat failed", { title: "Chat failed" });
    }
  };

  const runImage = async (
    text: string,
    opts: { step?: number; signal?: AbortSignal; imageUrl?: string | null } = {},
  ) => {
    const sendId = `image_${Date.now()}`;
    trackFunnel("generation_start", { surface: "image", model: imageModel, dedupeKey: sendId });
    const aid = appendAssistantPlaceholder("image", opts.step);
    let fallbackUsed = false;
    try {
      // Prefer an explicit per-call imageUrl (e.g. an attachment dataUrl
      // from a block runner) over the imageSource state, which is set
      // via setState just above the call and won't be visible through
      // closure on this same tick.
      let sourceUrl = opts.imageUrl !== undefined ? opts.imageUrl : imageSource;
      // Retry transient invoke failures (network blip, cold start, 5xx).
      // Only retry when there's an attachment, since that's the path
      // where large payloads + cold-start most often surface as
      // "Failed to send a request to the Edge Function". User-facing
      // errors (quota, 4xx) are NOT retried — they're surfaced once.
      const maxAttempts = sourceUrl ? 3 : 1;
      // When we have an attachment, after the initial retries we try
      // one last fallback pass with a downscaled / re-encoded copy of
      // the source. This rescues the common "payload too large /
      // failed to send" failure mode where the data URL is megabytes.
      let fallbackUsed = false;
      let data: any = null;
      let lastErr: Error | null = null;
      const totalPasses = sourceUrl ? maxAttempts + 1 : maxAttempts;
      for (let attempt = 1; attempt <= totalPasses; attempt++) {
        if (opts.signal?.aborted) throw new Error("Cancelled");
        try {
          const res = await supabase.functions.invoke("hub-image", {
            body: { prompt: text, model: imageModel, imageUrl: sourceUrl },
          });
          if (res.error) throw res.error;
          if (res.data?.error) throw new Error(res.data.error);
          data = res.data;
          lastErr = null;
          break;
        } catch (e) {
          const err = e as Error & { status?: number; context?: { status?: number } };
          const status = err.status ?? err.context?.status;
          const msg = String(err?.message ?? "");
          // Don't retry client-side / non-transient errors.
          const retryable =
            !status ||
            status >= 500 ||
            status === 413 ||
            /failed to (send|fetch)|network|timeout|load failed|payload|too large/i.test(msg);
          lastErr = err;
          if (!retryable || attempt === totalPasses) throw err;
          // Last pass before giving up: if we have an attachment and
          // haven't downscaled yet, rebuild it smaller and try once
          // more. This is the "re-read the attachment" fallback.
          const isLastRetryBeforeFallback =
            sourceUrl && !fallbackUsed && attempt === maxAttempts;
          if (isLastRetryBeforeFallback) {
            fallbackUsed = true;
            updateAssistant(aid, {
              content: `🖼️ Resizing attachment and retrying…`,
              streaming: true,
            });
            try {
              const smaller = await downscaleDataUrl(sourceUrl, 1280, 0.82);
              if (smaller && smaller !== sourceUrl) sourceUrl = smaller;
            } catch {
              // fall through with the original sourceUrl
            }
            continue;
          }
          const backoff = 500 * Math.pow(2, attempt - 1); // 500ms, 1s
          updateAssistant(aid, {
            content: `🖼️ Retrying image generation (attempt ${attempt + 1}/${totalPasses})…`,
            streaming: true,
          });
          await new Promise((r) => setTimeout(r, backoff));
        }
      }
      if (lastErr) throw lastErr;
      // The image endpoint runs to completion (no streaming abort), so
      // if the user cancelled while it was inflight, mark the result
      // as cancelled instead of dropping it into the transcript as if
      // it had been part of the plan.
      if (opts.signal?.aborted) {
        updateAssistant(aid, {
          content: `🖼️ Generated image (cancelled run)`,
          image: data?.image ?? null,
          model: imageModel,
          streaming: false,
          cancelled: true,
        });
        return;
      }
      updateAssistant(aid, {
        content: `🖼️ Generated image for: _${text}_`,
        image: data?.image ?? null,
        model: imageModel,
        streaming: false,
      });
      trackFunnel("generation_output", { surface: "image", model: imageModel, dedupeKey: sendId });
      emitFirstOutput("image");
    } catch (e) {
      const err = e as Error;
      const isFallbackExhausted = fallbackUsed;
      const fallbackFailedMessage =
        "🖼️ **Image attachment failed** — we retried and downscaled it, but the server still couldn't process it.\n\n" +
        "**Likely reason:** the original file is too large, heavily compressed, or the image service is temporarily overloaded.\n\n" +
        "**Next steps:**\n" +
        "- Try generating without the attachment\n" +
        "- Save the image to your device and upload a smaller version (under 2 MB)\n" +
        "- Wait a moment and try again";
      updateAssistant(aid, {
        content: isFallbackExhausted
          ? fallbackFailedMessage
          : `_Image failed: ${err.message}_`,
        streaming: false,
      });
      toastError(err.message || "Image generation failed", { title: "Image failed" });
    }
  };

  const runResearch = async (
    text: string,
    opts: { step?: number; signal?: AbortSignal } = {},
  ) => {
    const sendId = `research_${Date.now()}`;
    trackFunnel("generation_start", { surface: "research", model: researchModel, dedupeKey: sendId });
    const aid = appendAssistantPlaceholder("research", opts.step);
    let acc = "";
    let cites: string[] = [];
    try {
      await streamSSE({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hub-research`,
        body: { query: text, model: researchModel },
        onDelta: (chunk) => {
          acc += chunk;
          updateAssistant(aid, { content: acc, model: researchModel });
        },
        onMeta: (m) => {
          if (m && Array.isArray((m as { citations?: unknown }).citations)) {
            cites = (m as { citations: string[] }).citations;
            updateAssistant(aid, { citations: cites });
          }
        },
        onUsage: (usage) => updateAssistant(aid, { usage }),
        signal: opts.signal,
      });
      updateAssistant(aid, {
        content: acc,
        citations: cites,
        model: researchModel,
        streaming: false,
      });
      trackFunnel("generation_output", { surface: "research", model: researchModel, dedupeKey: sendId });
      emitFirstOutput("research");
    } catch (e) {
      const err = e as Error;
      if (opts.signal?.aborted || err.name === "AbortError") {
        updateAssistant(aid, {
          content: acc,
          citations: cites,
          streaming: false,
          cancelled: true,
          model: researchModel,
        });
        return;
      }
      updateAssistant(aid, {
        content: acc || `_Research failed: ${err.message}_`,
        streaming: false,
      });
      toastError(err.message || "Research failed", { title: "Research failed" });
    }
  };

  const runAnalyze = async (
    text: string,
    opts: { step?: number; signal?: AbortSignal } = {},
  ) => {
    const aid = appendAssistantPlaceholder("analyze", opts.step);
    let acc = "";
    try {
      await streamSSE({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hub-analyze`,
        body: { content: text, task: analyzeTask },
        onDelta: (chunk) => {
          acc += chunk;
          updateAssistant(aid, { content: acc });
        },
        onUsage: (usage) => updateAssistant(aid, { usage }),
        signal: opts.signal,
      });
      updateAssistant(aid, { content: acc, streaming: false });
      emitFirstOutput("analyze");
    } catch (e) {
      const err = e as Error;
      if (opts.signal?.aborted || err.name === "AbortError") {
        updateAssistant(aid, {
          content: acc,
          streaming: false,
          cancelled: true,
        });
        return;
      }
      updateAssistant(aid, {
        content: acc || `_Analyze failed: ${err.message}_`,
        streaming: false,
      });
      toastError(err.message || "Analyze failed", { title: "Analyze failed" });
    }
  };

  // Builder mode: a small "compose a better prompt" wrapper that asks the
  // current chat model to rewrite the user's draft as a structured prompt
  // (role / context / output format). Result drops into the chat AND the
  // composer so the user can immediately re-send it as a real chat turn.
  const runBuilder = async (
    text: string,
    opts: { step?: number; signal?: AbortSignal } = {},
  ) => {
    const aid = appendAssistantPlaceholder("chat", opts.step);
    const system =
      "You are a prompt engineer. Take the user's rough request and rewrite it as a clear, structured prompt with: a role line, 2-3 context bullets, the task in one imperative sentence, and an OUTPUT format spec. Return only the rewritten prompt — no preamble.";
    let acc = "";
    try {
      await streamSSE({
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hub-chat`,
        body: {
          messages: [
            { role: "system", content: system },
            { role: "user", content: text },
          ],
          model: chatModel,
        },
        onDelta: (chunk) => {
          acc += chunk;
          updateAssistant(aid, { content: acc, model: chatModel });
        },
        onUsage: (usage) => updateAssistant(aid, { usage }),
        signal: opts.signal,
      });
      updateAssistant(aid, { content: acc, model: chatModel, streaming: false });
      // Drop the compiled prompt into the composer so they can fire it.
      setComposer(acc.trim());
      toast.success("Prompt drafted — edit and send to run it");
    } catch (e) {
      const err = e as Error;
      if (opts.signal?.aborted || err.name === "AbortError") {
        updateAssistant(aid, {
          content: acc,
          model: chatModel,
          streaming: false,
          cancelled: true,
        });
        return;
      }
      updateAssistant(aid, { content: `_Builder failed: ${err.message}_`, streaming: false });
      toastError(err.message || "Prompt builder failed", { title: "Builder failed" });
    }
  };

  // ---- Playbook strip helpers ---------------------------------------

  const playbookSteps = playbook?.steps ?? [];
  const fillPrompt = (raw: string) =>
    raw.replace(/\{topic\}/g, topic.trim() || "(your topic)");

  // Track which playbook steps the user has triggered (loaded into the
  // composer or run via "next task"). Surfaces as checkmarks on the
  // step strip and a thin progress bar above it so the user can always
  // see where they are in the playbook from inside the chat.
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  // Reset whenever the playbook itself changes — different playbook,
  // different progress.
  useEffect(() => {
    setCompletedSteps(new Set());
  }, [playbook?.slug]);
  const markStepDone = (idx: number) =>
    setCompletedSteps((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });

  const totalPlaybookSteps = playbookSteps.length;
  const doneStepCount = completedSteps.size;
  const progressPct =
    totalPlaybookSteps > 0
      ? Math.round((doneStepCount / totalPlaybookSteps) * 100)
      : 0;
  const currentStepIndex = useMemo(() => {
    for (let i = 0; i < totalPlaybookSteps; i++) {
      if (!completedSteps.has(i)) return i;
    }
    return Math.max(0, totalPlaybookSteps - 1);
  }, [completedSteps, totalPlaybookSteps]);

  // Mirror the active step into tutor context so "what do I do next?" answers
  // know exactly where the user is. Includes step number for clarity.
  useEffect(() => {
    if (!playbook || totalPlaybookSteps === 0) return;
    const step = playbookSteps[currentStepIndex];
    if (!step) return;
    setTutorContext({
      currentStep: `Step ${currentStepIndex + 1} of ${totalPlaybookSteps} — ${step.title}`,
    });
  }, [playbook, currentStepIndex, totalPlaybookSteps, playbookSteps]);

  // Mirror the tail of the transcript into tutor context so the Tutor FAB
  // can answer with continuity — e.g. reference what the user just asked
  // or what the assistant most recently produced. We strip streaming
  // placeholders, drop empty turns, cap to the last 4 turns, and clip each
  // turn to ~600 chars (the edge function re-clips again to be safe).
  useEffect(() => {
    const recent = turns
      .filter((t) => {
        if (!t.content || !t.content.trim()) return false;
        if (t.role === "assistant" && t.streaming) return false;
        return true;
      })
      .slice(-4)
      .map((t) => ({
        role: t.role,
        content: t.content.slice(0, 600),
      }));
    setTutorContext({ recentMessages: recent });
  }, [turns]);

  const handleStepClick = (idx: number) => {
    const step = playbookSteps[idx];
    if (!step) return;
    setMode("chat");
    setChatModel(step.model);
    setComposer(fillPrompt(step.prompt));
    markStepDone(idx);
    toast.success(`Loaded step ${idx + 1} → ${step.modelLabel}`);
  };

  /** Open the next-task composer for a given step. */
  const openNextTaskFor = (idx: number) => {
    setNextTaskStepIdx(idx);
    setNextTaskOpen(true);
    markStepDone(idx);
  };

  /**
   * Run a list of NextTaskBlocks in order against the shared transcript.
   * Each block reuses the existing mode runner so memory carries forward
   * (chat blocks call runChat with the full historyForChat, which already
   * includes every prior assistant turn — image / research / analyze
   * blocks append their own assistant turns so subsequent chat blocks
   * see them in history). We mutate the per-mode model setters before
   * each block so the runner picks up the per-block model choice.
   */
  // Step-level variables exposed to every block prompt as `{name}`.
  // Per-block extras (most importantly `{source}`) are merged in at
  // run time inside `runBlocks` so each block can resolve to its own
  // attached file. Keeping this as a memo keeps the modal's chip list
  // stable across re-renders.
  const nextTaskVariables = useMemo<Record<string, string | undefined>>(() => {
    const stepIdx = nextTaskStepIdx;
    const step = playbookSteps[stepIdx];
    return {
      topic: topic.trim() || undefined,
      step: String(stepIdx + 1),
      stepTitle: step?.title,
    };
  }, [topic, nextTaskStepIdx, playbookSteps]);

  const runBlocks = async (blocks: NextTaskBlock[]) => {
    setBusy(true);
    // Spin up a fresh AbortController so the "Cancel remaining" pill
    // can interrupt the in-flight stream and stop subsequent blocks.
    // We stash it in a ref so the cancel handler outside this closure
    // can reach it.
    const controller = new AbortController();
    runAbortRef.current = controller;
    const stepNumber = nextTaskStepIdx + 1;
    // Block kind → human label for the status pill / per-bubble badge.
    const kindLabel: Record<NextTaskBlock["kind"], string> = {
      chat: "Chat",
      image: "Image",
      research: "Research",
      analyze: "Analyze",
      builder: "Prompt builder",
    };
    try {
      for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        if (!block.prompt.trim()) continue;
        // Bail before starting the next block if the user pressed cancel
        // while the previous one was streaming. Anything we already
        // wrote into the transcript stays as-is.
        if (controller.signal.aborted) break;
        setRunStatus({
          step: stepNumber,
          currentIndex: i,
          total: blocks.length,
          currentKindLabel: kindLabel[block.kind],
        });
        // Resolve `{topic}`, `{source}`, etc. just before running so
        // the prompt the user composed in the modal becomes the prompt
        // the model actually sees. Step-level vars (topic/step/title)
        // come from the shell; per-block vars (source) come from the
        // block's own attachment via blockScopedVariables.
        const resolvedPrompt = applyVariables(block.prompt, {
          ...nextTaskVariables,
          ...blockScopedVariables(block),
        });
        // If the block carries an attachment, weave it into the prompt
        // / runtime so the underlying mode runner actually uses it.
        // - image: feed the data URL as imageSource (the existing
        //   edit-from-image path), and keep the prompt as the
        //   instruction.
        // - analyze: prepend the file contents (text-likes inline,
        //   binary references appear as a [Attachment] note) so the
        //   analyze endpoint operates on the file the user picked.
        // - research: append a one-line context note so the model
        //   knows the user is grounding the question in a doc.
        const att = block.attachment;
        let promptForRun = resolvedPrompt;
        let promptForTranscript = resolvedPrompt;
        if (att) {
          const sizeKb = Math.max(1, Math.round(att.size / 1024));
          const chip = `📎 ${att.name} (${sizeKb} KB)`;
          promptForTranscript = `${chip}\n\n${resolvedPrompt}`;
          if (block.kind === "analyze") {
            if (att.text) {
              promptForRun = `${resolvedPrompt}\n\n--- Attached file: ${att.name} ---\n${att.text}`;
            } else {
              promptForRun = `${resolvedPrompt}\n\n[Attached file: ${att.name} (${att.mime}). The file is referenced but its binary contents could not be inlined.]`;
            }
          } else if (block.kind === "research") {
            promptForRun = att.text
              ? `${resolvedPrompt}\n\nGround the answer in this attached document (${att.name}):\n${att.text.slice(0, 6000)}`
              : `${resolvedPrompt}\n\n[User attached a file for context: ${att.name} (${att.mime}).]`;
          }
        }
        appendUser(promptForTranscript);
        const runOpts = { step: stepNumber, signal: controller.signal };
        if (block.kind === "chat") {
          setChatModel(block.model);
          setMode("chat");
          await runChat(promptForRun, runOpts);
        } else if (block.kind === "image") {
          setImageModel(block.model);
          setMode("image");
          // For image blocks, route the attachment data URL through
          // the existing imageSource state AND pass it directly to
          // runImage. setState is async, so the closure in runImage
          // wouldn't see the new imageSource on this tick — the
          // explicit opts.imageUrl avoids that race.
          if (att?.dataUrl) setImageSource(att.dataUrl);
          await runImage(promptForRun, { ...runOpts, imageUrl: att?.dataUrl ?? undefined });
        } else if (block.kind === "research") {
          setResearchModel(block.model);
          setMode("research");
          await runResearch(promptForRun, runOpts);
        } else if (block.kind === "analyze") {
          // For analyze, the "model" slot carries the task id.
          setAnalyzeTask(block.model);
          setMode("analyze");
          await runAnalyze(promptForRun, runOpts);
        } else if (block.kind === "builder") {
          setChatModel(block.model);
          setMode("builder");
          await runBuilder(promptForRun, runOpts);
        }
      }
    } finally {
      runAbortRef.current = null;
      setRunStatus(null);
      setBusy(false);
    }
  };

  /** User-triggered cancel of an in-flight multi-block run. Aborts the
   *  current stream (the runner catches AbortError and tags the bubble
   *  as cancelled) and short-circuits the remaining blocks. Already
   *  completed bubbles stay in the transcript. */
  const cancelRun = () => {
    const ctrl = runAbortRef.current;
    if (!ctrl) return;
    ctrl.abort();
    toast("Cancelled remaining blocks. Completed results kept.", { icon: "✋" });
  };

  // ---- Image source upload ------------------------------------------

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSource(reader.result as string);
    reader.readAsDataURL(f);
  };

  // ---- Render --------------------------------------------------------

  const ModeIcon = MODES.find((m) => m.id === mode)?.icon ?? MessageSquare;

  return (
    <div className="relative rounded-xl border border-border bg-card/40 backdrop-blur-sm flex flex-col overflow-hidden h-[78vh]">
      {/* Floating "+N points → context" badge — Phase 6 dopamine. */}
      <InShellXPBadge context={playbook?.title ?? MODES.find((m) => m.id === mode)?.label} />
      {/* "Loaded from …" pill — surfaces when a chat seed was just
          consumed (e.g. tapping a challenge item on /build/.../quests/…). */}
      {seedPill && (
        <div className="px-3 py-1 text-[10px] font-mono text-primary bg-primary/5 border-b border-primary/20 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          <span className="truncate">
            Loaded from <span className="font-semibold">{seedPill.label}</span>
          </span>
          <button
            type="button"
            onClick={() => setSeedPill(null)}
            aria-label="Dismiss seed source"
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {/* Guest preview banner — only renders for anonymous visitors, with
          two visual states: counting down (informative) and gated (a wall
          that opens the signup modal). Hides entirely once signed in. */}
      {isGuest && (
        <div
          className={`px-3 py-1.5 text-[11px] font-mono border-b flex items-center gap-2 ${
            guestGated
              ? "bg-primary/10 border-primary/40 text-foreground"
              : "bg-secondary/40 border-border text-muted-foreground"
          }`}
        >
          <Lock className="w-3 h-3 text-primary shrink-0" />
          {guestGated ? (
            <>
              <span className="truncate">
                You've used your <span className="text-primary font-semibold">{GUEST_FREE_LIMIT} free preview messages</span>.
                Sign up to unlock the full playbook.
              </span>
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                className="ml-auto shrink-0 px-2 py-0.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Unlock free
              </button>
            </>
          ) : (
            <>
              <span className="truncate">
                Guest preview ·{" "}
                <span className="text-foreground font-semibold">
                  {guestRemaining}
                </span>{" "}
                free message{guestRemaining === 1 ? "" : "s"} left before signup.
              </span>
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                className="ml-auto shrink-0 underline hover:text-primary"
              >
                Sign up
              </button>
            </>
          )}
        </div>
      )}
      {/* ---- Top mode bar --------------------------------------------- */}
      <div className="flex items-center gap-1 px-2 sm:px-3 py-2 border-b border-border bg-background/50 overflow-x-auto">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                setMode(m.id);
                setPendingSuggestion(null);
              }}
              title={m.hint}
              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono transition-colors ${
                active
                  ? "bg-primary/15 text-primary border border-primary/40"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{m.label}</span>
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* New chat — wipes the transcript so the user can start a
              fresh task / quest / challenge without leaving the shell.
              Confirms before nuking a non-trivial conversation. */}
          <button
            type="button"
            onClick={() => {
              if (busy) return;
              if (
                turns.length > 1 &&
                !window.confirm(
                  "Start a new chat? Your current conversation will be cleared from view (saved chats stay in History).",
                )
              ) {
                return;
              }
              // Stop any in-flight multi-block run before resetting.
              if (runAbortRef.current) {
                try {
                  runAbortRef.current.abort();
                } catch {
                  /* noop */
                }
                runAbortRef.current = null;
                setRunStatus(null);
              }
              setTurns([]);
              setComposer("");
              setSeedPill(null);
              clearTutorContext();
            }}
            disabled={busy}
            title="Start a new chat (clears the current transcript)"
            aria-label="Start a new chat"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[11px] font-mono text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden md:inline">New chat</span>
          </button>

          {/* History / Chat list — opens the in-chat Library dialog on
              the "recent" tab so the user can jump between saved chats
              that track tasks, quests, and challenges. */}
          <button
            type="button"
            onClick={() => {
              setLibraryDialogTab("recent");
              setLibraryDialogQuery("");
              setLibraryDialogOpen(true);
            }}
            title="Browse your saved chats — tasks, quests, challenges"
            aria-label="Open chat history"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[11px] font-mono text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
          >
            <History className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Chats</span>
          </button>

          {/* Model picker — only relevant for chat / builder; image &
              research have their own pickers in the mode panel. */}
          {(mode === "chat" || mode === "builder" || mode === "library") && (
            <div className="flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 text-primary" />
              <select
                value={chatModel}
                onChange={(e) => setChatModel(e.target.value)}
                className="bg-secondary border border-border rounded-md px-2 py-1 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary max-w-[160px]"
                title="Switch model — your conversation memory carries forward"
              >
                {CHAT_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {playbook && (
            <button
              type="button"
              onClick={() => toggleSaved(playbook.slug, topic)}
              title={isSaved(playbook.slug) ? "Saved to library" : "Save to library"}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-[11px] font-mono text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
            >
              {isSaved(playbook.slug) ? (
                <BookmarkCheck className="w-3.5 h-3.5 text-primary" />
              ) : (
                <Bookmark className="w-3.5 h-3.5" />
              )}
              <span className="hidden md:inline">
                {isSaved(playbook.slug) ? "Saved" : "Save"}
              </span>
            </button>
          )}

          {onExit && (
            <button
              type="button"
              onClick={onExit}
              aria-label="Exit chat"
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ---- Optional playbook strip --------------------------------- */}
      {playbook && playbookSteps.length > 0 && (
        <div className="px-3 py-2 border-b border-border bg-primary/5 flex flex-col gap-1.5">
          {/* Title row + visible progress indicator (Step X of Y + bar) */}
          <div className="flex items-start sm:items-center gap-2 flex-wrap">
            <span className="text-base shrink-0 leading-none mt-0.5 sm:mt-0" aria-hidden>
              {playbook.emoji}
            </span>
            <span
              className="text-[11px] font-mono font-semibold text-foreground min-w-0 flex-1 sm:flex-none sm:max-w-[420px] break-words leading-snug"
              title={playbook.title}
            >
              {playbook.title}
            </span>
            <span
              className="text-[10px] font-mono text-muted-foreground whitespace-nowrap shrink-0"
              aria-live="polite"
            >
              Step{" "}
              <span className="text-primary font-semibold">
                {currentStepIndex + 1}
              </span>{" "}
              of{" "}
              <span className="text-foreground">{totalPlaybookSteps}</span>
              <span className="hidden sm:inline">
                {" "}
                · {doneStepCount} done
              </span>
            </span>
            <div
              className="h-1 flex-1 max-w-[180px] rounded-full bg-secondary overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalPlaybookSteps}
              aria-valuenow={doneStepCount}
              aria-label={`Playbook progress: ${doneStepCount} of ${totalPlaybookSteps} steps complete`}
            >
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Step pills with checkmarks for completed steps */}
          <div className="flex items-center gap-1 overflow-x-auto">
            {playbookSteps.map((s, i) => {
              const done = completedSteps.has(i);
              const isCurrent = i === currentStepIndex && !done;
              return (
              // Each step gets two affordances: a quick "load this step
              // into the composer" pill (one tap → ready to send) and a
              // smaller "next task" button that opens the composer modal
              // so the user can stack blocks (image + research + chat
              // etc.) for that step. The modal route preserves memory
              // across LLM switches by running everything against the
              // same shared transcript.
              <span
                key={i}
                className={`shrink-0 inline-flex items-stretch rounded-full border overflow-hidden transition-colors ${
                  done
                    ? "border-primary/40 bg-primary/10"
                    : isCurrent
                      ? "border-primary/60 bg-background/60 ring-1 ring-primary/30"
                      : "border-border bg-background/60"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleStepClick(i)}
                  title={`Step ${i + 1}: ${s.title} (${s.modelLabel})${done ? " · ✓ done" : ""}`}
                  aria-label={`Step ${i + 1}: ${s.title}${done ? ", completed" : ""}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono transition-colors ${
                    done
                      ? "text-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {done ? (
                    <Check
                      className="w-3 h-3 text-primary"
                      aria-hidden
                    />
                  ) : (
                    <span
                      className={`font-semibold ${
                        isCurrent ? "text-primary" : "text-primary/70"
                      }`}
                    >
                      {i + 1}
                    </span>
                  )}
                  <span className="max-w-[220px] truncate hidden md:inline" title={s.title}>{s.title}</span>
                </button>
                <button
                  type="button"
                  onClick={() => openNextTaskFor(i)}
                  title={`Build next task for step ${i + 1}`}
                  aria-label={`Build next task for step ${i + 1}`}
                  className="inline-flex items-center justify-center px-1.5 border-l border-border/60 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                >
                  <ListChecks className="w-3 h-3" />
                </button>
              </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- AI Playbook Checklist (depth-aware, step-by-step) ------- */}
      {/*
        Render an AI-generated, structured checklist for the current
        task whenever we have a usable anchor (a static playbook, a seed
        pill from a quest/challenge handoff, or an explicit topic) AND
        the user is signed in (the edge function is JWT-gated).

        The checklist is independent from the static `playbook` strip:
        the static strip lists the canonical curated steps, while this
        one is the live AI suggestion, regenerable per depth. Clicking
        Run on a step seeds the composer in chat mode.
      */}
      {user && (playbook?.title || seedPill?.label || topic) && (
        <div className="px-3 py-2 border-b border-border bg-background/20">
          <PlaybookChecklist
            surface={
              playbook?.slug ??
              seedPill?.source ??
              `topic:${(topic || "").slice(0, 48)}`
            }
            kind={
              chatType === "challenge"
                ? "daily-hack"
                : chatType === "quest"
                  ? "quest"
                  : chatType === "task"
                    ? "mastery"
                    : "quest"
            }
            title={playbook?.title || seedPill?.label || topic || "Current task"}
            description={playbook?.tagline || undefined}
            context={topic || undefined}
            difficulty={(playbook as unknown as { difficulty?: number | string })?.difficulty}
            onRunStep={(step) => {
              setMode("chat");
              setComposer(step.prompt);
              toast.success(`Loaded step ${step.index + 1}`, {
                description: step.title,
              });
            }}
          />
        </div>
      )}

      {/* ---- Mode-specific helper bar (above transcript) ------------- */}
      {mode === "image" && (
        <div className="px-3 py-2 border-b border-border bg-background/30 flex flex-wrap items-center gap-2">
          <select
            value={imageModel}
            onChange={(e) => setImageModel(e.target.value)}
            className="bg-secondary border border-border rounded-md px-2 py-1 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary"
          >
            {(imageRemixOnly ? IMAGE_MODELS.filter((m) => m.caps.includes("Remixes")) : IMAGE_MODELS).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setImageRemixOnly((v) => !v)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-mono transition-colors ${
              imageRemixOnly
                ? "bg-primary/10 border-primary/40 text-primary"
                : "bg-secondary border-border text-muted-foreground hover:text-foreground"
            }`}
            title={imageRemixOnly ? "Show all models" : "Show only remix-capable models"}
          >
            <ImageIcon className="w-3 h-3" />
            Remix only
          </button>
          {IMAGE_MODELS.find((m) => m.id === imageModel)?.caps.map((cap) => (
            <span
              key={cap}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-mono"
            >
              {cap}
            </span>
          ))}
          <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed border-border text-[10px] font-mono text-muted-foreground cursor-pointer hover:border-primary/40 hover:text-primary transition-colors">
            <Upload className="w-3 h-3" />
            {imageSource ? "Change source" : "Upload to edit"}
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </label>
          {imageSource && (
            <button
              type="button"
              onClick={() => setImageSource(null)}
              className="text-[10px] font-mono text-muted-foreground hover:text-destructive"
            >
              clear
            </button>
          )}
        </div>
      )}

      {mode === "research" && (
        <div className="px-3 py-2 border-b border-border bg-background/30 flex flex-wrap items-center gap-2">
          <select
            value={researchModel}
            onChange={(e) => setResearchModel(e.target.value)}
            className="bg-secondary border border-border rounded-md px-2 py-1 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary"
          >
            {RESEARCH_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          <span className="text-[10px] font-mono text-muted-foreground">
            Web-grounded — citations show inline
          </span>
        </div>
      )}

      {mode === "analyze" && (
        <div className="px-3 py-2 border-b border-border bg-background/30 flex flex-wrap items-center gap-2">
          <select
            value={analyzeTask}
            onChange={(e) => setAnalyzeTask(e.target.value)}
            className="bg-secondary border border-border rounded-md px-2 py-1 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary"
          >
            {ANALYZE_TASKS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="text-[10px] font-mono text-muted-foreground">
            Paste text below; output drops into chat
          </span>
        </div>
      )}

      {mode === "builder" && (
        <div className="px-3 py-2 border-b border-border bg-background/30 flex items-center gap-2">
          <Wand2 className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-mono text-muted-foreground">
            Type a rough idea — we'll rewrite it as a structured prompt and drop it into your composer.
          </span>
        </div>
      )}

      {/* ---- Composer (pinned at TOP) -------------------------------- */}
      {/* Composer renders ABOVE the transcript so the input is always
          in view without scrolling. The transcript reverses turn order
          (newest first) so the latest reply appears right under the
          composer instead of being pushed off-screen. */}
      <div className="border-b border-border bg-background/50 p-3">
        <CreditOverageWarning
          turns={turns
            .filter((t) => (t.role === "user" || t.role === "assistant") && t.content)
            .map((t) => ({ role: t.role, content: t.content }))}
          composer={composer}
          model={chatModel}
        />
        {/* Chat-type selector — only shown while the transcript is
            empty so the type for an in-flight chat can't be silently
            swapped (which would re-derive the title and confuse the
            Recent list grouping). Once the first turn lands, the chip
            row is replaced by a tiny static badge. */}
        {turns.length === 0 ? (
          <div className="mb-2 flex flex-wrap items-center gap-1">
            <span className="text-[10px] font-mono text-muted-foreground mr-1">Type:</span>
            {CHAT_TYPES.map((opt) => {
              const active = chatType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setChatType(opt.value)}
                  title={opt.hint}
                  className={`px-2 py-0.5 rounded-md font-mono text-[11px] border transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-card/50 text-muted-foreground hover:text-primary hover:border-primary/40"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : chatType !== "open" ? (
          <div className="mb-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] border border-border bg-card/50 text-muted-foreground">
            Type: <span className="text-primary">{CHAT_TYPES.find((o) => o.value === chatType)?.label}</span>
          </div>
        ) : null}
        {/* Recommender chip — shown when the recommender wants to
            switch *mode* (image / research / analyze / builder).
            Image mode gets a richer confirmation card: image generation
            burns credits and produces a visible artifact, so we show a
            prompt preview and require an explicit "Generate" tap. The
            user can also keep editing the prompt in the composer below
            before confirming. Other modes keep the lightweight one-tap
            chip — they're cheap and fully reversible. */}
        {pendingSuggestion?.mode === "image" ? (
          <div
            className="mb-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2"
            role="dialog"
            aria-live="polite"
            aria-label="Confirm image generation"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-mono text-foreground">
                  Looks like you want to <span className="text-primary font-semibold">generate an image</span>. Confirm before we burn credits:
                </p>
                <p className="mt-1 text-[11px] font-mono text-muted-foreground italic line-clamp-3 break-words">
                  "{composer.trim() || "(empty prompt — type one below)"}"
                </p>
                <p className="mt-1 text-[10px] font-mono text-muted-foreground/80">
                  Tip: edit the prompt in the box below before generating.
                </p>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-end gap-1.5">
              <button
                type="button"
                onClick={() => setPendingSuggestion(null)}
                className="px-2 py-0.5 rounded font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Keep as chat
              </button>
              <button
                type="button"
                disabled={!composer.trim() || busy}
                onClick={acceptPendingSuggestion}
                className="px-2.5 py-0.5 rounded font-mono text-[11px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate image
              </button>
            </div>
          </div>
        ) : pendingSuggestion?.mode && (
          <div
            className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-2 min-w-0">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <p className="text-[11px] font-mono text-foreground leading-snug">
                {pendingSuggestion.reason}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto">
              <button
                type="button"
                onClick={acceptPendingSuggestion}
                className="px-2 py-0.5 rounded font-mono text-[11px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Switch to{" "}
                {MODES.find((m) => m.id === pendingSuggestion.mode)?.label ??
                  pendingSuggestion.mode}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingSuggestion(null);
                  void send();
                }}
                aria-label="Dismiss suggestion and send anyway"
                className="px-2 py-0.5 rounded font-mono text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Send anyway
              </button>
            </div>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder={
              mode === "chat"
                ? "Ask anything — switch models above without losing context…"
                : mode === "image"
                  ? "Describe the image you want…"
                  : mode === "research"
                    ? "What do you want to research?"
                    : mode === "analyze"
                      ? "Paste text or code to analyze…"
                      : mode === "builder"
                        ? "What's the rough idea? We'll turn it into a structured prompt…"
                        : "Ask anything…"
            }
            className="flex-1 resize-none bg-secondary border border-border rounded-lg px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !composer.trim()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-mono font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 self-stretch"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="hidden sm:inline">{busy ? "" : "Send"}</span>
          </button>
        </div>
        <div className="mt-1.5 text-[10px] font-mono text-muted-foreground flex items-center gap-2 flex-wrap">
          <Sparkles className="w-3 h-3 text-primary" />
          {mode === "chat" || mode === "builder" || mode === "library"
            ? `Will run on ${CHAT_MODELS.find((m) => m.id === chatModel)?.label ?? chatModel}`
            : mode === "image"
              ? `Will generate with ${IMAGE_MODELS.find((m) => m.id === imageModel)?.label ?? imageModel}`
              : mode === "research"
                ? `Will search with ${RESEARCH_MODELS.find((m) => m.id === researchModel)?.label ?? researchModel}`
                : `Will analyze: ${ANALYZE_TASKS.find((t) => t.id === analyzeTask)?.label ?? analyzeTask}`}
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => { setMvpInitialIdea(composer.trim()); setMvpOpen(true); }}
            className="text-primary/80 hover:text-primary"
            title="Type /mvp to open the MVP generator with your message as the idea"
          >
            try /mvp
          </button>
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => {
              setLibraryDialogTab("library");
              setLibraryDialogQuery(composer.trim());
              setLibraryDialogOpen(true);
            }}
            className="text-primary/80 hover:text-primary"
            title="Type /library to browse community entries without leaving chat"
          >
            /library
          </button>
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => {
              setLibraryDialogTab("recent");
              setLibraryDialogQuery("");
              setLibraryDialogOpen(true);
            }}
            className="text-primary/80 hover:text-primary"
            title="Type /recent to jump back into a saved chat"
          >
            /recent
          </button>
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="inline-flex items-center gap-1 text-primary/80 hover:text-primary"
            title="Open the command palette (⌘K / Ctrl+K) — search tools, library, people"
          >
            <CommandIcon className="w-3 h-3" />
            ⌘K
          </button>
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => {
              setTurns((prev) => [
                ...prev,
                {
                  id: newId(),
                  role: "assistant",
                  content: HELP_MESSAGE_BODY,
                  kind: "chat",
                },
              ]);
            }}
            className="text-primary/80 hover:text-primary"
            title="Type /help to list the top actions you can run from chat"
          >
            /help
          </button>
          <span className="opacity-60">·</span>
          <button
            type="button"
            onClick={() => {
              setComposer("/hint");
              setTimeout(() => void send(), 0);
            }}
            className="text-primary/80 hover:text-primary"
            title="Type /hint to unlock the next stepwise hint for today's daily challenge"
          >
            /hint
          </button>
          <span className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSaveOpen(true)}
              disabled={turns.length === 0}
              title="Save this conversation as a playbook + tag mastery skills"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <BookmarkPlus className="w-3 h-3" />
              Save as playbook
            </button>
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-3 flex-wrap text-[10px] font-mono">
          <InShellUsageMeter
            turnCount={turns.filter((t) => t.role === "user").length}
            completedAssistantCount={
              turns.filter((t) => t.role === "assistant" && !t.streaming).length
            }
          />
          <CreditBurnTicker />
        </div>
      </div>

      {/* ---- Run-status banner (above transcript) ------------------- */}
      {runStatus && (
        <div className="px-3 sm:px-4 py-2 border-b border-primary/30 bg-primary/5 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
          <span className="text-[11px] font-mono text-foreground truncate">
            <span className="text-primary font-semibold">
              Step {runStatus.step} · {runStatus.currentKindLabel}
            </span>{" "}
            <span className="text-muted-foreground">
              ({runStatus.currentIndex + 1} of {runStatus.total} blocks)
            </span>
          </span>
          <button
            type="button"
            onClick={cancelRun}
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 text-[10px] font-mono transition-colors"
            title="Stop the current block and skip the rest. Completed results stay."
          >
            <StopCircle className="w-3 h-3" />
            Cancel remaining
          </button>
        </div>
      )}

      {/* ---- Transcript (newest first, flows down) ------------------ */}
      <div
        ref={transcriptRef}
        className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-3"
      >
        {turns.length === 0 && (
          <div className="text-center py-12">
            <ModeIcon className="w-8 h-8 mx-auto mb-3 text-primary/60" />
            <div className="text-sm font-mono text-foreground mb-1">
              {mode === "chat" && "Talk to any model. Switch mid-thread — memory is preserved."}
              {mode === "image" && "Describe an image — it'll appear right here in the conversation."}
              {mode === "research" && "Ask a research question — answers come with web citations."}
              {mode === "analyze" && "Paste text below to summarize, explain, or extract."}
              {mode === "builder" && "Type a rough idea; we'll turn it into a sharp prompt."}
              {mode === "library" && "Forkable playbooks coming inline — for now, ask the chat what to fork."}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">
              Switching modes never clears your chat.
            </div>
          </div>
        )}

        {/* Only the *latest* completed assistant turn gets the
            "try next" strip — older turns stay clean and the user
            isn't bombarded with chips up the whole transcript. */}
        {(() => null)()}
        {/* Composer is pinned at the TOP of the shell, so we render the
            transcript in REVERSE order — newest turn first, sitting just
            under the composer. `isLastAssistant` is computed against the
            original (chronological) list so the "try next" strip stays
            attached to the genuinely most-recent assistant reply. */}
        {[...turns].reverse().map((t) => {
          const origIdx = turns.indexOf(t);
          const isLastAssistant =
            t.role === "assistant" &&
            !turns.slice(origIdx + 1).some((later) => later.role === "assistant");
          if (t.role === "user") {
            return (
              <div key={t.id} className="ml-8 rounded-lg px-3 py-2 bg-primary/10 border border-primary/30">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                  you
                </div>
                <div className="text-sm text-foreground whitespace-pre-wrap">{t.content}</div>
              </div>
            );
          }
          // assistant
          const modelTag = t.model ? t.model.split("/").pop() : t.kind;
          const blockKindLabel =
            t.kind === "chat"
              ? "Chat"
              : t.kind === "image"
                ? "Image"
                : t.kind === "research"
                  ? "Research"
                  : t.kind === "analyze"
                    ? "Analyze"
                    : "assistant";
          return (
            <div
              key={t.id}
              className={`mr-8 rounded-lg px-3 py-2 bg-card border ${
                t.cancelled ? "border-destructive/40" : "border-border"
              }`}
            >
              {/* Step badge — only renders when this turn was emitted as
                  part of a Next-Task multi-block run, so the user can
                  trace each bubble back to a specific playbook step. */}
              {typeof t.step === "number" && (
                <div className="mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/15 text-primary text-[10px] font-mono">
                  Step {t.step} · {blockKindLabel}
                </div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2">
                <span>{t.kind ?? "assistant"}</span>
                {modelTag && (
                  <span className="px-1.5 py-0.5 rounded bg-secondary text-foreground/80 normal-case tracking-normal">
                    {modelTag}
                  </span>
                )}
                {t.streaming && (
                  <span className="flex items-center gap-1 text-primary normal-case tracking-normal">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    streaming…
                  </span>
                )}
                {t.cancelled && (
                  <span className="flex items-center gap-1 text-destructive normal-case tracking-normal">
                    <StopCircle className="w-3 h-3" />
                    cancelled
                  </span>
                )}
              </div>
              {t.image && (
                <img
                  src={t.image}
                  alt="generated"
                  className="w-full max-w-md rounded-md border border-border my-2"
                />
              )}
              {t.content && (
                <div className="prose dark:prose-invert prose-sm max-w-none prose-pre:bg-secondary prose-pre:border prose-pre:border-border prose-code:text-primary">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {t.content || "▍"}
                  </ReactMarkdown>
                </div>
              )}
              {t.citations && t.citations.length > 0 && (
                <ol className="mt-2 pt-2 border-t border-border/60 space-y-0.5 text-[10px] font-mono">
                  {t.citations.map((c, i) => (
                    <li key={i} className="truncate">
                      <span className="text-muted-foreground">[{i + 1}]</span>{" "}
                      <a
                        href={c}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {c}
                      </a>
                    </li>
                  ))}
                </ol>
              )}
              {t.usage && !t.streaming && (
                <MessageCostBreakdown
                  model={t.model ?? chatModel}
                  usage={t.usage}
                />
              )}
              {/* Light "try next" strip — only on the most recent
                  completed chat reply, never on streaming/cancelled/
                  empty/non-chat turns. Pure heuristics, zero AI cost. */}
              {isLastAssistant &&
                !t.streaming &&
                !t.cancelled &&
                t.content &&
                t.kind === "chat" && (
                  <NextStepSuggestions
                    currentModel={chatModel}
                    nextPlaybookStep={
                      playbook && playbookSteps[currentStepIndex]
                        ? {
                            stepNumber: currentStepIndex + 1,
                            title: playbookSteps[currentStepIndex].title,
                            prompt: fillPrompt(
                              playbookSteps[currentStepIndex].prompt,
                            ),
                          }
                        : null
                    }
                    onPickPrompt={(p) => setComposer(p)}
                    onPickModel={(m) => setChatModel(m)}
                  />
                )}
            </div>
          );
        })}
      </div>

      {/* Next-task composer modal (mounted once; opens for the chosen
          step). Submitting runs every block against the shared
          transcript so cross-LLM memory is preserved. */}
      {playbook && playbookSteps[nextTaskStepIdx] && (
        <NextTaskModal
          open={nextTaskOpen}
          onOpenChange={setNextTaskOpen}
          stepNumber={nextTaskStepIdx + 1}
          stepTitle={playbookSteps[nextTaskStepIdx].title}
          initialPrompt={fillPrompt(playbookSteps[nextTaskStepIdx].prompt)}
          playbookSlug={playbook.slug}
          variables={nextTaskVariables}
          onSubmit={runBlocks}
        />
      )}

      {/* Save-as-playbook dialog — single instance, opened from the
          composer footer. We pass the current transcript so the dialog
          can both display a derived title and persist the messages. */}
      <SaveAsPlaybookDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        turns={turns
          .filter((t) => t.content.trim())
          .map((t) => ({
            role: t.role,
            content: t.content,
            model: t.role === "assistant" ? t.model : undefined,
          }))}
        defaultTitle={playbook ? `${playbook.title} · ${topic || "session"}` : ""}
        primaryModel={chatModel}
      />

      {/* Guest signup modal — opens when the gate is hit or when the user
          taps the "Sign up" / "Unlock free" CTA in the banner. After a
          successful signup the AuthProvider flips `user` and the gate /
          banner disappear on the next render. */}
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode="signup"
      />
      {/* `/mvp` slash command target — opens the same MVP generator the
          Agents Builder header uses, with the typed idea pre-filled. */}
      <GenerateMvpDialog
        open={mvpOpen}
        onClose={() => setMvpOpen(false)}
        initialIdea={mvpInitialIdea}
      />
      {/* `/library` and `/recent` slash command target — secondary
          surfaces (filters, featured/trending/newest shelves, recent
          chats) live behind a chat command instead of a separate page,
          so the chat stays the single home for everything. */}
      <ChatLibraryDialog
        open={libraryDialogOpen}
        onClose={() => setLibraryDialogOpen(false)}
        initialTab={libraryDialogTab}
        initialQuery={libraryDialogQuery}
      />
      {/* In-chat command palette — ⌘K / Ctrl+K. Searches tools, library
          entries, and public profiles, plus shell actions (browse
          library, recent chats, save playbook, new chat, generate MVP).
          Tool / entry rows expose a secondary "fork into chat" action
          that seeds the composer instead of navigating away. */}
      <ChatCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenLibrary={(tab, q) => {
          setLibraryDialogTab(tab);
          setLibraryDialogQuery(q ?? "");
          setLibraryDialogOpen(true);
        }}
        onOpenMvp={(idea) => {
          setMvpInitialIdea(idea);
          setMvpOpen(true);
        }}
        onSavePlaybook={() => setSaveOpen(true)}
        canSavePlaybook={turns.length > 0}
        onNewChat={() => {
          setTurns([]);
          setComposer("");
          setSeedPill(null);
        }}
        onSeedComposer={(text) => setComposer(text)}
      />
    </div>
  );
}
